import { useState } from "react";
import { Button } from "@/components/wensity/button";
import { Input } from "@/components/wensity/input";
import { Switch } from "@/components/wensity/switch";
import { Card, CardHeader } from "@/components/wensity/card";
import {
  Dialog,
  DialogConfirmation,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogScrollBody,
  DialogTitle,
} from "@/components/wensity/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/wensity/table";
import type { BackgroundBlob, BackgroundConfig } from "@/data/site";

function emptyBlob(): BackgroundBlob {
  return {
    colorStart: "#cd1c18",
    colorEnd: "#ff8a73",
    size: 0.6,
    top: 30,
    left: 30,
    blur: 80,
    opacity: 0.4,
    duration: 30,
    delay: 0,
    reverse: false,
  };
}

interface BackgroundEditorProps {
  background: BackgroundConfig;
  onChange: (background: BackgroundConfig) => void;
}

export function BackgroundEditor({ background, onChange }: BackgroundEditorProps) {
  const [dialogIndex, setDialogIndex] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<BackgroundBlob>(emptyBlob());
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  function openNew() {
    setDraft(emptyBlob());
    setDialogIndex("new");
  }

  function openEdit(index: number) {
    setDraft(background.blobs[index]);
    setDialogIndex(index);
  }

  function handleSave() {
    if (dialogIndex === "new") {
      onChange({ ...background, blobs: [...background.blobs, draft] });
    } else if (typeof dialogIndex === "number") {
      onChange({
        ...background,
        blobs: background.blobs.map((blob, i) => (i === dialogIndex ? draft : blob)),
      });
    }
    setDialogIndex(null);
  }

  function handleDelete(index: number) {
    onChange({ ...background, blobs: background.blobs.filter((_, i) => i !== index) });
    setDeleteIndex(null);
  }

  return (
    <div className="space-y-6">
      <Card className="border-[var(--color-border)] bg-[rgba(20,24,22,0.72)]">
        <CardHeader className="space-y-4">
          <Switch
            label="Grain overlay"
            description="Adds a subtle texture that prevents color banding."
            checked={background.grain}
            onCheckedChange={(grain) => onChange({ ...background, grain })}
          />
          <Input
            label="Overall opacity"
            hint="0 (invisible) to 1 (fully visible)"
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={background.containerOpacity}
            onChange={(event) => onChange({ ...background, containerOpacity: Number(event.target.value) })}
          />
        </CardHeader>
      </Card>

      <div className="flex justify-end">
        <Button variant="brand" onClick={openNew}>
          Add blob
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Colors</TableHead>
            <TableHead>Size</TableHead>
            <TableHead>Position</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {background.blobs.map((blob, index) => (
            <TableRow key={index}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="h-6 w-6 shrink-0 rounded-full border border-[var(--color-border)]"
                    style={{ background: `linear-gradient(135deg, ${blob.colorStart}, ${blob.colorEnd})` }}
                  />
                  <span className="font-mono text-xs text-[var(--color-muted)]">
                    {blob.colorStart} → {blob.colorEnd}
                  </span>
                </div>
              </TableCell>
              <TableCell>{Math.round(blob.size * 100)}%</TableCell>
              <TableCell>
                {blob.top}% / {blob.left}%
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(index)}>
                    Edit
                  </Button>
                  <DialogConfirmation
                    open={deleteIndex === index}
                    onOpenChange={(open) => setDeleteIndex(open ? index : null)}
                    trigger={
                      <Button variant="destructive" size="sm">
                        Delete
                      </Button>
                    }
                    title="Delete this blob?"
                    confirmLabel="Delete"
                    destructive
                    onConfirm={() => handleDelete(index)}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {background.blobs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-[var(--color-muted)]">
                No blobs — the background will render flat.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      <Dialog open={dialogIndex !== null} onOpenChange={(open) => !open && setDialogIndex(null)}>
        <DialogContent variant="scrollable">
          <DialogHeader>
            <DialogTitle>{dialogIndex === "new" ? "New blob" : "Edit blob"}</DialogTitle>
          </DialogHeader>
          <DialogScrollBody className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start color"
                type="color"
                value={draft.colorStart}
                onChange={(event) => setDraft((d) => ({ ...d, colorStart: event.target.value }))}
              />
              <Input
                label="End color"
                type="color"
                value={draft.colorEnd}
                onChange={(event) => setDraft((d) => ({ ...d, colorEnd: event.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Size"
                hint="0 to 1"
                type="number"
                min={0.1}
                max={1.5}
                step={0.05}
                value={draft.size}
                onChange={(event) => setDraft((d) => ({ ...d, size: Number(event.target.value) }))}
              />
              <Input
                label="Blur (px)"
                type="number"
                min={0}
                step={5}
                value={draft.blur}
                onChange={(event) => setDraft((d) => ({ ...d, blur: Number(event.target.value) }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Top (%)"
                type="number"
                min={0}
                max={100}
                value={draft.top}
                onChange={(event) => setDraft((d) => ({ ...d, top: Number(event.target.value) }))}
              />
              <Input
                label="Left (%)"
                type="number"
                min={0}
                max={100}
                value={draft.left}
                onChange={(event) => setDraft((d) => ({ ...d, left: Number(event.target.value) }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Opacity"
                hint="0 to 1"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={draft.opacity}
                onChange={(event) => setDraft((d) => ({ ...d, opacity: Number(event.target.value) }))}
              />
              <Input
                label="Duration (s)"
                hint="One morph cycle"
                type="number"
                min={4}
                step={1}
                value={draft.duration}
                onChange={(event) => setDraft((d) => ({ ...d, duration: Number(event.target.value) }))}
              />
            </div>
            <Input
              label="Delay (s)"
              hint="Negative values pre-roll the animation"
              type="number"
              step={1}
              value={draft.delay}
              onChange={(event) => setDraft((d) => ({ ...d, delay: Number(event.target.value) }))}
            />
            <Switch
              label="Reverse rotation"
              checked={draft.reverse}
              onCheckedChange={(reverse) => setDraft((d) => ({ ...d, reverse }))}
            />
          </DialogScrollBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogIndex(null)}>
              Cancel
            </Button>
            <Button variant="brand" onClick={handleSave}>
              {dialogIndex === "new" ? "Add blob" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
